/**
 * Welcome to the main entry point of the app. In this file, we'll
 * be kicking off our app
 *
 * Most of this file is boilerplate and you shouldn't need to modify
 * it very often. But take some time to look through and understand
 * what is going on here.
 *
 * The app navigation resides in ./app/navigation, so head over there
 * if you're interested in adding screens and navigators.
 */
import "./i18n";
import React, { useState, useEffect, useRef, FunctionComponent as Component } from "react";
import { NavigationContainerRef } from "@react-navigation/native";
import { SafeAreaProvider, initialWindowMetrics } from "react-native-safe-area-context";
import * as SplashScreen from "expo-splash-screen";

import * as storage from "./utils/storage";
import { useBackButtonHandler, RootNavigator, canExit, setRootNavigation } from "./navigation";
import { Provider } from "react-redux";

import { Provider as PaperProvider } from "react-native-paper";
import { StyleSheet, Platform } from "react-native";
import { theme } from "./theme/theme";
import {
  useFonts,
  Lato_100Thin,
  Lato_100Thin_Italic,
  Lato_300Light,
  Lato_300Light_Italic,
  Lato_400Regular,
  Lato_400Regular_Italic,
  Lato_700Bold,
  Lato_700Bold_Italic,
  Lato_900Black,
  Lato_900Black_Italic,
} from "@expo-google-fonts/lato";

// This puts screens in a native ViewController or Activity. If you want fully native
// stack navigation, use `createNativeStackNavigator` in place of `createStackNavigator`:
// https://github.com/kmagiera/react-native-screens#using-native-stack-navigator
import { enableScreens } from "react-native-screens";
import Amplify, { Auth } from "aws-amplify";
import configureStore from "./redux/store";
import config from "../config";
import { getAuthData } from "./utils/getAuthData";
import { DataDogLogger } from "./utils/logger";
// Polyfill for Intl until properly supported in Android
import "intl";
import "intl/locale-data/jsonp/en";
import { RootState } from "./redux/reducers";
import { StorageKeys } from "./enums/storage-keys";
import Auryc from "rn-auryc-sdk";
import Heap from "@heap/react-native-heap";

Amplify.configure({
  Auth: {
    region: "us-east-1",
    userPoolId: config.userPoolId,
    userPoolWebClientId: config.userPoolClientId,
    authenticationFlowType: "USER_PASSWORD_AUTH",
    mandatorySignIn: true,
  },
});
import { MenuProvider } from "react-native-popup-menu";

enableScreens(false);

export const NAVIGATION_PERSISTENCE_KEY = "NAVIGATION_STATE";

function App() {
  const [isReady, setReady] = useState(false);
  const [store, setStore] = useState(false);

  const [fontsLoaded] = useFonts({
    Lato_100Thin,
    Lato_100Thin_Italic,
    Lato_300Light,
    Lato_300Light_Italic,
    Lato_400Regular,
    Lato_400Regular_Italic,
    Lato_700Bold,
    Lato_700Bold_Italic,
    Lato_900Black,
    Lato_900Black_Italic,
    Lato: Lato_400Regular,
  });

  const navigationRef = useRef<NavigationContainerRef>();

  setRootNavigation(navigationRef as any);
  useBackButtonHandler(navigationRef as any, canExit);

  // Use effect to store desired cookies to local storage.
  useEffect(() => {
    (async () =>
      await storage.loadFromCookieAndStore(StorageKeys.WEB_APP_ACCESS_TOKEN_COOKIE, true, true))();
    (async () =>
      await storage.loadFromCookieAndStore(StorageKeys.WEB_APP_ID_TOKEN_COOKIE, true, true))();
    (async () =>
      await storage.loadFromCookieAndStore(StorageKeys.WEB_APP_REFRESH_TOKEN_COOKIE, true, true))();

    //Initializes Heap and Auryc for mobile.
    //Web is initialized in web/index.html.
    //User data is set in hooks/useHeapAnalytics.ts for both mobile and web
    if (Platform.OS !== "web") {
      const logger = new DataDogLogger({});
      try {
        if (config.env === "prd") {
          Heap.setAppId("3519395024");
          Auryc.initialize("e2a0eb32a1f5931dd6c0b0f281e8af60", "9829-Main-prod-heap");
        } else {
          Heap.setAppId("1383840494");
          Auryc.initialize("8da25693ed122bc896c6760e8a4264cb", "8821-Development-prod-heap");
        }
      } catch (error) {
        logger.info("Unable to initialize Heap and Auryc");
        logger.error(error);
      }
    }
  }, []);

  // Kick off initial async loading actions, like loading fonts and RootStore
  useEffect(() => {
    (async () => {
      const logger = new DataDogLogger({});
      const storagePromises = [
        storage.load(StorageKeys.HAS_SEEN_TUTORIAL),
        storage.load(StorageKeys.HAS_SEEN_INTRO),
      ];
      const [hasSeenTutorial, hasSeenIntro] = await Promise.all(storagePromises);
      const { userInfo, cognitoUser } = await getAuthData(logger);
      const initialStore: RootState = {
        auth: {
          userInfo,
          cognitoUser,
          applicationRelevantStorageInformation: { hasSeenTutorial, hasSeenIntro },
        },
        updates: { availableUpdateInfo: false },
        reservation: { reservations: [] },
        payment: { status: null },
        logger,
        appReview: {
          username: "",
          userReview: "",
          dateCreated: "",
          dateUpdated: "",
          nextRunDays: 0,
        },
        userRole: {},
      };
      setReady(true);

      setStore(configureStore(initialStore) as any);
      try {
        await SplashScreen.hideAsync();
      } catch (error) {
        logger.error(error);
      }
    })();
  }, []);
  if (!store || !isReady || !theme || !fontsLoaded) return null;

  return (
    <PaperProvider theme={theme}>
      <Provider store={store as any}>
        <SafeAreaProvider initialMetrics={initialWindowMetrics}>
          <MenuProvider customStyles={{ backdrop: { backgroundColor: "#121212", opacity: 0.24 } }}>
            <RootNavigator ref={navigationRef as any} logger={store.logger} />
          </MenuProvider>
        </SafeAreaProvider>
      </Provider>
    </PaperProvider>
  );
}
export default App;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
});
